import { AsyncPipe } from '@angular/common';
import type { OnInit, QueryList } from '@angular/core';
import { ChangeDetectionStrategy, Component, ViewChildren, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { BehaviorSubject, catchError, combineLatest, filter, map, NEVER, Subject } from 'rxjs';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import type { User } from '@core/account-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import { filterDefined } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { JobItem, OfferSimpleListItem } from '@core/offers-types';
import type { TalentPoolBrief } from '@core/talent-pools-types';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  HeadingComponent,
  IconComponent,
  ScrollableDirective,
} from '@core/user-interface-legacy';

import type {
  AccessOptionsConfig,
  ShareOnTheGoDialogData,
  ShareOnTheGoDialogResult,
  ShareOnTheGoI18n,
  UserAccessValue,
} from './types';
import { AccessValue, SHARE_ON_THE_GO_I18N } from './types';
import { UserItemComponent } from './user-item/user-item.component';

type ShareOnTheGoDialogComponentData = ShareOnTheGoDialogData & { allSelectedUsers: User[] };

@Component({
  selector: 'rt-share-on-the-go-dialog',
  templateUrl: './share-on-the-go-dialog.component.html',
  styleUrls: ['./share-on-the-go-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    TranslatePipe,
    ButtonComponent,
    HeadingComponent,
    ReactiveFormsModule,
    AsyncPipe,
    UserItemComponent,
    IconComponent,
    ScrollableDirective,
    ButtonLoadingDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareOnTheGoDialogComponent
  implements DialogComponentI<ShareOnTheGoDialogComponentData, ShareOnTheGoDialogResult>, OnInit
{
  private readonly store = inject(Store);
  private readonly dialogRef = inject<DialogRef<ShareOnTheGoDialogComponent>>(DialogRef);
  public readonly shareOnTheGoI18n = inject<ShareOnTheGoI18n>(SHARE_ON_THE_GO_I18N);

  public data: ShareOnTheGoDialogComponentData | null = null;
  public offer$: Observable<OfferSimpleListItem> | null = null;
  public usersWithAccess$: Observable<User[]> | null = null;
  public usersWithAccessOpen$ = new BehaviorSubject<boolean>(false);
  public accessOptionsConfigs$: Observable<AccessOptionsConfig[]> | null = null;
  public loading$ = new Subject<boolean>();
  public submitEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  @ViewChildren(UserItemComponent)
  private userItems: QueryList<UserItemComponent>;

  private currentUserAccessibleOfferIds$ = this.store.select(CurrentUserSelectors.data).pipe(
    filterDefined,
    map(user => user.membership?.offerIds ?? []),
  );

  private get accessValues(): UserAccessValue[] {
    return <UserAccessValue[]>this.userItems
        ?.filter(userItem => userItem.selectedOption.value !== null)
        ?.map(userItem => ({
          user: userItem.user(),
          accessValue: userItem.selectedOption.value,
        })) ?? [];
  }

  public trackById<T extends { id: number }>(_: number, item: T): number {
    return item.id;
  }

  public setDialogData(data: ShareOnTheGoDialogComponentData): void {
    this.data = data;
  }

  public ngOnInit(): void {
    this.offer$ = this.getOffer$();
    this.usersWithAccess$ = this.getUsersWithAccess$();
    this.accessOptionsConfigs$ = this.getAccessOptionsConfigs$();
  }

  public submit(): void {
    this.loading$.next(true);
    this.dialogRef.close({ userAccessValues: this.accessValues });
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public onUserItemValueChanges(): void {
    if (!this.data?.disableSubmitOnSkipAll) {
      return;
    }

    const noSelectedUsersHaveAccess =
      this.data.allSelectedUsers.length === this.data.usersToShareWith.length;
    const skipAllUsers: boolean = this.accessValues.length
      ? this.accessValues.every(
          userAccessValue => userAccessValue.accessValue === AccessValue.DontRequest,
        ) && noSelectedUsersHaveAccess
      : false;

    this.submitEnabled$.next(!skipAllUsers);
  }

  private buildAccessOptionsConfig({
    abilityMap,
    currentUserAccessibleOfferIds,
    userToShareWith,
  }: {
    abilityMap: Record<Ability, boolean>;
    currentUserAccessibleOfferIds: number[];
    userToShareWith: User;
  }): AccessOptionsConfig | never {
    if (!abilityMap[Ability.ShareCandidates] && !abilityMap[Ability.AssignUsersToJobs]) {
      throw new Error('User does not have access to share on the go, but dialog was opened!');
    }
    return {
      [AccessValue.CandidateOnly]: {
        isEnabled: abilityMap[Ability.ShareCandidates],
        isVisible: true,
      },
      [AccessValue.AllCandidatesInOffer]: {
        isEnabled:
          abilityMap[Ability.AssignUsersToJobs] &&
          (currentUserAccessibleOfferIds.includes(this.data!.offerId ?? -1) ||
            abilityMap[Ability.ViewAllJobs]) &&
          userToShareWith.roleAbilities.includes(Ability.ViewJobs),
        isVisible: true,
      },
      [AccessValue.DontRequest]: {
        isEnabled: true,
        isVisible: (this.data?.usersToShareWith?.length ?? 0) > 1,
      },
    };
  }

  private getOffer$(): Observable<JobItem | TalentPoolBrief> | null {
    return <Observable<JobItem | TalentPoolBrief>>this.store
      .select(
        FullCandidateSelectors.currentUserAvailablePlacements({
          candidateId: this.data!.candidateId,
        }),
      )
      .pipe(
        map(placements => {
          const placement = placements.find(
            placement =>
              (placement.offerId || placement.talentPoolId) ===
              (this.data!.offerId || this.data!.talentPoolId),
          );
          return placement?.offer || placement?.talentPool;
        }),
        filter(offer => !!offer),
      );
  }

  private getAccessOptionsConfigs$(): Observable<AccessOptionsConfig[]> | null {
    return combineLatest([
      this.store.select(AbilitiesSelectors.abilityMap),
      this.currentUserAccessibleOfferIds$,
    ]).pipe(
      map(
        ([abilityMap, currentUserAccessibleOfferIds]) =>
          this.data?.usersToShareWith?.map(userToShareWith =>
            this.buildAccessOptionsConfig({
              abilityMap,
              currentUserAccessibleOfferIds,
              userToShareWith,
            }),
          ) ?? [],
      ),
      catchError(err => {
        console.warn(err.message);
        this.cancel();
        return NEVER;
      }),
    );
  }

  private getUsersWithAccess$(): Observable<User[]> | null {
    return combineLatest([
      this.store.select(
        FullCandidateSelectors.offerUsersWithAccess({
          candidateId: this.data!.candidateId,
          offerId: this.data?.offerId ?? -1,
          talentPoolId: this.data?.talentPoolId ?? -1,
        }),
      ),
      this.store.select(CurrentUserSelectors.data).pipe(filterDefined),
    ]).pipe(
      map(([usersWithAccess, currentUser]) =>
        usersWithAccess.filter(
          userWithAccess =>
            this.wasUserSelected(userWithAccess) && userWithAccess.id !== currentUser.id,
        ),
      ),
    );
  }

  private wasUserSelected(userWithAccess: User): boolean {
    return !!this.data?.allSelectedUsers
      .map(userToShareWith => userToShareWith.id)
      ?.includes(userWithAccess.id);
  }
}
